const apiUrl = process.env.REACT_APP_API_URL;

function getBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
}

function currentAdminUserId() {
  // The super admin's real id (root id when impersonating).
  return localStorage.getItem('userId1') || localStorage.getItem('userId');
}

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Accept: '*/*',
    Authorization: getBasicAuthHeader(),
  };
}

async function parseError(response, fallback) {
  let message = fallback || `Request failed: ${response.status}`;
  try {
    const body = await response.json();
    if (body?.message) message = body.message;
  } catch (_) {
    /* ignore */
  }
  return new Error(message);
}

/** Lists all activation codes (newest first) with live redemption counts. */
export async function fetchActivationCodes() {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/activation-codes?adminUserId=${encodeURIComponent(adminUserId)}`,
    { method: 'GET', headers: authHeaders() },
  );
  if (!response.ok) {
    throw await parseError(response, `Activation codes request failed: ${response.status}`);
  }
  return response.json();
}

/** All organizations ({ orgId, orgName }) for the code form's org picker. */
export async function fetchOrganizations() {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/activation-codes/orgs?adminUserId=${encodeURIComponent(adminUserId)}`,
    { method: 'GET', headers: authHeaders() },
  );
  if (!response.ok) {
    throw await parseError(response, `Org list failed: ${response.status}`);
  }
  return response.json();
}

/** Asks the backend for a fresh random code string (for the generate button). */
export async function generateActivationCode() {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/activation-codes/generate?adminUserId=${encodeURIComponent(adminUserId)}`,
    { method: 'GET', headers: authHeaders() },
  );
  if (!response.ok) {
    throw await parseError(response, 'Could not generate a code.');
  }
  const body = await response.json();
  return body.code;
}

/**
 * Creates an activation code. `payload` carries: code, planId, orgId, applicability,
 * validFrom (epoch s), validUntil (epoch s), maxTotalUses, active.
 */
export async function createActivationCode(payload) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(`${apiUrl}/api/admin/activation-codes`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ adminUserId, ...payload }),
  });
  if (!response.ok) {
    throw await parseError(response, 'Create failed');
  }
  return response.json();
}

/** Updates an existing code identified by its activationCodeId. */
export async function updateActivationCode(activationCodeId, payload) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/activation-codes/${encodeURIComponent(activationCodeId)}`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ adminUserId, ...payload }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, 'Update failed');
  }
  return response.json();
}

/** Permanently deletes an activation code. Already-granted plans are unaffected. */
export async function deleteActivationCode(activationCodeId) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/activation-codes/${encodeURIComponent(activationCodeId)}?adminUserId=${encodeURIComponent(adminUserId)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!response.ok) {
    throw await parseError(response, 'Delete failed');
  }
  return response.json();
}

/** Toggles a code's active flag. */
export async function setActivationCodeActive(activationCodeId, active) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/admin/activation-codes/${encodeURIComponent(activationCodeId)}/active`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ adminUserId, active: !!active }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, 'Update failed');
  }
  return response.json();
}
